import React, { createContext, useContext, useEffect, useState, useCallback } from "react";

const BASE = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "lp_admin_token";

const AdminAuthContext = createContext(null);

export function AdminAuthProvider({ children }) {
  const [token, setToken] = useState(() => localStorage.getItem(TOKEN_KEY) || "");
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async (t) => {
    if (!t) {
      setAdmin(null);
      setLoading(false);
      return;
    }
    try {
      const res = await fetch(`${BASE}/api/admin/me`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!res.ok) throw new Error(`me: ${res.status}`);
      const data = await res.json();
      setAdmin(data);
    } catch {
      localStorage.removeItem(TOKEN_KEY);
      setToken("");
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe(token);
  }, [token, fetchMe]);

  const login = useCallback(async (email, password) => {
    const res = await fetch(`${BASE}/api/admin/auth/login`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(err.detail || "Sign-in failed.");
    }
    const data = await res.json();
    localStorage.setItem(TOKEN_KEY, data.token);
    setToken(data.token);
    setAdmin(data.admin);
    return data.admin;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem(TOKEN_KEY);
    setToken("");
    setAdmin(null);
  }, []);

  // Tiny helper so child components don't repeat the auth header.
  // Skips the default JSON content-type for FormData bodies (file uploads) —
  // forcing it there strips the browser's own multipart boundary and the
  // server can't parse the file field at all.
  const authFetch = useCallback((path, options = {}) => {
    const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
    return fetch(`${BASE}${path}`, {
      ...options,
      headers: {
        ...(isFormData ? {} : { "Content-Type": "application/json" }),
        ...(options.headers || {}),
        Authorization: `Bearer ${token}`,
      },
    });
  }, [token]);

  return (
    <AdminAuthContext.Provider value={{ token, admin, loading, login, logout, authFetch, refreshMe: () => fetchMe(token) }}>
      {children}
    </AdminAuthContext.Provider>
  );
}

export function useAdminAuth() {
  const ctx = useContext(AdminAuthContext);
  if (!ctx) throw new Error("useAdminAuth must be inside <AdminAuthProvider>");
  return ctx;
}

// Returns true ONLY when the admin's role-set is exactly {bookkeeper}.
// Mirrors the backend `bookkeeper_readonly` middleware so the UI can hide
// write controls and prevent confusing 403s.
export function useIsBookkeeperReadOnly() {
  const { admin } = useAdminAuth();
  if (!admin) return false;
  const roles = new Set(admin.roles || (admin.role ? [admin.role] : []));
  if (roles.has("super_admin")) return false;
  return roles.size === 1 && roles.has("bookkeeper");
}
